import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormProvider } from 'react-hook-form';
import {
  AsyncSelect,
  FormCell,
  FormLayout,
  FormRow,
  FormSection,
  RichTextArea,
  Select,
  TextInput,
} from '~/components';
import { loadCategoryOptions } from '~/features/vault/profile/api';
import { useCustomForm } from '~/hooks/useCustomForm';
import { SubspecialtyFields } from '../../../components/SubspecialtyFields';
import { useSetProviderCategory } from '../../../hooks/useSetProviderCategory';
import { useResetSubspecialties } from '../../../hooks/useSubspecialties';
import { useJobListingActorRef } from '../../jobListingProvider';
import type { Context as JobListingContext } from '../../types';
import {
  EMPLOYMENT_CLASSIFICATION_OPTIONS,
  emptyBasicInfoForm,
  JOB_TYPE_OPTIONS,
  WORK_LOCATION_OPTIONS,
} from './constants';
import { BasicInfoFormSchema, BasicInfoFormValues } from './types';
import { loadAOIOptionByProviderCategory, transformApiToBasicInfoForm } from './utils';

const TAGS = CT.employer.jobListings.basicInfoForm;

export const BasicInfoForm = ({ footer }: { footer: React.ReactNode }) => {
  const jobListingRef = useJobListingActorRef();
  const { basicInfoRef, modal, listing } = useSelector(jobListingRef, (s): JobListingContext => {
    if (typeof s === 'object' && s !== null && 'context' in s) {
      return s.context as JobListingContext;
    }
    throw new Error('Invalid state: context not found');
  });

  const methods = useCustomForm<BasicInfoFormValues>({
    resolver: zodResolver(BasicInfoFormSchema),
    defaultValues: listing ? transformApiToBasicInfoForm(listing) : emptyBasicInfoForm,
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
  } = methods;

  const onSubmitSuccess = (values: BasicInfoFormValues) => {
    if (listing?.id && isDirty) {
      basicInfoRef.send({ type: 'ON_UPDATE', payload: { values } });
    } else if (!modal.edit && !listing?.id) {
      basicInfoRef.send({ type: 'ON_ADD', payload: { values } });
    } else {
      jobListingRef.send({ type: 'ON_NEXT' });
    }
  };

  const onSubmitError = (err: unknown) => {
    console.log('Basic Info Form Submit Error: ', err, methods.getValues());
  };

  useSetProviderCategory(listing, (providerCategory) =>
    setValue('providerCategory', providerCategory)
  );

  useResetSubspecialties(methods, {
    resetGeneralFlag: true,
    clearSpecialtyWithCategory: { empty: { label: '', value: '' } },
  });

  const providerCategory = watch('providerCategory');
  const specialtyId = watch('aoiSpecialty')?.value;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <FormSection componentsPerLine={1}>
            <FormRow>
              <TextInput
                name={'jobTitle'}
                placeholder="e.g. Emergency Medicine Physician - Night Shift Lead"
                cyTag={TAGS.listingTitle}
                label={'Job Title'}
                required
              />
            </FormRow>
            <FormRow>
              {/* [MCV-1586] QA raised this on the B2B listing wizard, but this
                form is that wizard's near-twin and had the identical two-row
                default on the identical field. Sizing only the B2B copy would
                leave the two description fields visibly different for no
                reason a user could infer. */}
              <RichTextArea
                name={'jobDescription'}
                description="Do note include identifiable patient or provider information."
                cyTag={TAGS.listingDescription}
                label={'Job Description'}
              />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <AsyncSelect
                name={'providerCategory'}
                cyTag={TAGS.providerCategory}
                label={'Provider Category'}
                minChars={false}
                required
                loadOptions={loadCategoryOptions}
              />
            </FormRow>
            <FormRow>
              <AsyncSelect
                name={'aoiSpecialty'}
                key={providerCategory?.value}
                cyTag={TAGS.aoiSpecialty}
                label={'Provider Specialty'}
                minChars={false}
                required
                placeholder={
                  providerCategory?.label ? 'Select...' : 'Select A Provider Category First'
                }
                loadOptions={(inputValue: string) =>
                  loadAOIOptionByProviderCategory(providerCategory.value, inputValue)
                }
              />
            </FormRow>
            {specialtyId && (
              <FormCell span={12}>
                <SubspecialtyFields
                  specialtyId={specialtyId}
                  subspecialtiesCyTag={TAGS.subspecialties}
                  generalSpecialtyCyTag={TAGS.generalSpecialtyFlag}
                />
              </FormCell>
            )}
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <Select
                name={'jobType'}
                cyTag={TAGS.jobType}
                label={'Job Type'}
                options={JOB_TYPE_OPTIONS}
                required
              />
            </FormRow>
            <FormRow>
              <Select
                name={'employmentClassification'}
                cyTag={TAGS.employmentClassification}
                label={'Employment Classification'}
                options={EMPLOYMENT_CLASSIFICATION_OPTIONS}
                required
              />
            </FormRow>
            <FormRow>
              <Select
                name={'workLocation'}
                cyTag={TAGS.workLocation}
                label={'Work Location'}
                options={WORK_LOCATION_OPTIONS}
              />
            </FormRow>
          </FormSection>
          {footer}
        </FormLayout>
      </form>
    </FormProvider>
  );
};
